import { propertyDefinitionsByLabel } from "@buildwithflux/constants";
/**
 * Helper functions
 */
function truncateValueString(value, maxLength = 128) {
    if (value === undefined)
        return "";
    maxLength = Math.max(4, maxLength);
    const valueStr = String(value).trimStart();
    const truncated = valueStr.length > maxLength - 3 ? valueStr.substring(0, maxLength - 3) + "..." : valueStr;
    return truncated.replace(/\n/g, " ");
}
const SI_PREFIXES = [
    { threshold: 1e12, prefix: "T" },
    { threshold: 1e9, prefix: "G" },
    { threshold: 1e6, prefix: "M" },
    { threshold: 1e3, prefix: "k" },
    { threshold: 1, prefix: "" },
    { threshold: 1e-3, prefix: "m" },
    { threshold: 1e-6, prefix: "\u03BC" },
    { threshold: 1e-9, prefix: "n" },
    { threshold: 1e-12, prefix: "p" },
    { threshold: 1e-15, prefix: "f" },
];
/**
 * Format a numeric value with the appropriate SI prefix.
 * e.g. 10000 -> "10k", 0.000000000047 -> "47p"
 */
function formatWithSIPrefix(value) {
    if (value === 0)
        return "0";
    const absValue = Math.abs(value);
    for (const { threshold, prefix } of SI_PREFIXES) {
        if (absValue >= threshold) {
            const scaled = value / threshold;
            const formatted = Number.isInteger(scaled)
                ? scaled.toString()
                : parseFloat(scaled.toPrecision(4)).toString();
            return `${formatted}${prefix}`;
        }
    }
    return value.toString();
}
/**
 * Format a property value with its unit for display.
 * Handles SI prefix formatting for numbers and avoids duplicating units.
 */
function formatValueWithUnit(value, unit) {
    if (value === undefined || value === null)
        return "";
    if (typeof value === "number" && unit) {
        return `${formatWithSIPrefix(value)}${unit}`;
    }
    const strValue = String(value).trimEnd();
    if (unit && !strValue.endsWith(unit)) {
        return `${strValue}${unit}`;
    }
    return strValue;
}
/**
 * Given a single applied action, generate a summary text
 */
export function summarizeAppliedAction(action, appliedActionData) {
    let summary = "";
    if (action === "rewriteProjectDescription") {
        summary += `Updated **project description**`;
    }
    else if (action === "rewriteProjectProperties") {
        const count = Object.keys(appliedActionData.projectPropertiesAdded ?? {}).length +
            Object.keys(appliedActionData.projectPropertiesRemoved ?? {}).length;
        if (count > 0) {
            summary += `Updated **${count} document ${count == 1 ? "property" : "properties"}**`;
        }
    }
    else if (action === "rewriteDesignators") {
        const updatedElements = Object.keys(appliedActionData.componentDesignatorsUpdated ?? {}).length;
        const updatedNets = Object.keys(appliedActionData.netDesignatorsUpdated ?? {}).length;
        if (updatedElements || updatedNets) {
            const updated = [];
            if (updatedElements) {
                updated.push(`${updatedElements} component label${updatedElements == 1 ? "" : "s"}`);
            }
            if (updatedNets) {
                updated.push(`${updatedNets} net label${updatedNets == 1 ? "" : "s"}`);
            }
            summary += `Updated labels for **${updated.join(" and ")}**`;
        }
    }
    else if (action === "rewriteComponents") {
        const added = (appliedActionData.componentsAdded ?? []).length;
        const removed = (appliedActionData.componentsRemoved ?? []).length;
        const warnings = (appliedActionData.designatorWarnings ?? []).length;
        const changes = [];
        if (added) {
            changes.push(`Added **${added} component${added == 1 ? "" : "s"}**`);
        }
        if (removed) {
            changes.push(`Removed **${removed} component${removed == 1 ? "" : "s"}**`);
        }
        summary += changes.join(" and ");
        if (warnings) {
            summary += ` (${warnings} designator${warnings == 1 ? "" : "s"} reassigned)`;
        }
    }
    else if (action === "rewriteComponentProperties") {
        const numObjects = (appliedActionData.componentPropertiesUpdated ?? []).length;
        if (numObjects > 0) {
            summary += `Updated properties for **${numObjects} object${numObjects == 1 ? "" : "s"}**`;
        }
    }
    else if (action === "rewriteLayoutRules") {
        const count = (appliedActionData.pcbLayoutRulesUpdated ?? []).length;
        if (count > 0) {
            summary += `Updated **${count} layout rule${count == 1 ? "" : "s"}**`;
        }
    }
    else if (action === "rewriteProjectNets" || action === "rewriteConnections") {
        const count = (appliedActionData.netsUpdated ?? []).length;
        if (count > 0) {
            summary += `Updated **${count} connection${count == 1 ? "" : "s"}**`;
        }
    }
    return summary;
}
export function appliedActionContent(action, appliedActionData, document) {
    if (action === "rewriteComponents") {
        const addElementsMap = {};
        for (const componentInfo of appliedActionData.componentsAdded ?? []) {
            const element = document.elements[componentInfo.uid];
            if (!element)
                continue;
            const partName = element.part_version_data_cache.name;
            if (!addElementsMap[partName]) {
                addElementsMap[partName] = [];
            }
            addElementsMap[partName].push(element);
        }
        let content = "";
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        Object.keys(addElementsMap).forEach((key) => {
            const elements = addElementsMap[key];
            const element = elements[0];
            const labels = elements.map((element) => element.label).filter((label) => label && label.trim() !== "");
            const labelText = labels.length > 0 ? ` as ${labels.join(", ")}` : "";
            content += `- Added ${elements.length}x ${element?.part_version_data_cache.name}${labelText}\n`;
        });
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        appliedActionData.componentsRemoved?.forEach((componentInfo) => {
            content += `- Removed ${componentInfo.designator}\n`;
        });
        // Surface designator warnings so the agent knows about reassignments
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        appliedActionData.designatorWarnings?.forEach((warning) => {
            content += `- ⚠️ Requested designator ${warning.requested} was unavailable, assigned ${warning.assigned} instead\n`;
        });
        // Warn about connectivity loss when components are removed
        if ((appliedActionData.componentsRemoved?.length ?? 0) > 0) {
            content += `\n⚠️ Removed components had their net connections destroyed. Use the connections tool with a \`view\` action to verify connectivity and re-wire any lost connections.\n`;
        }
        return content;
    }
    else if (action === "rewriteProjectNets" || action === "rewriteConnections") {
        const netsConnected = (appliedActionData.netsUpdated ?? []).filter((n) => n.action === "connect");
        let content = "";
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        netsConnected.forEach((connection) => {
            const connections = connection.pins.map((p) => {
                return `${p.componentDesignator}:${p.pinDesignator}`;
            });
            const netSuffix = connection.netName ? ` to ^${connection.netName}` : "";
            if (connections.length === 2 && !connection.netName) {
                content += `- Connected ${connections[0]} to ${connections[1]}\n`;
            }
            else if (connections.length > 0) {
                content += `- Connected ${connections.join(", ")}${netSuffix}\n`;
            }
            else if (connection.netName) {
                content += `- Connected net ^${connection.netName}\n`;
            }
        });
        const netsDisconnected = (appliedActionData.netsUpdated ?? []).filter((n) => n.action === "disconnect");
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        netsDisconnected.forEach((disconnection) => {
            const connections = disconnection.pins.map((p) => {
                return `${p.componentDesignator}:${p.pinDesignator}`;
            });
            if (connections.length === 2) {
                content += `- Disconnected ${connections[0]} from ${connections[1]}\n`;
            }
            else {
                content += `- Disconnected ${connections.join(", ")}\n`;
            }
        });
        if (appliedActionData.netlistView) {
            content += `\n${appliedActionData.netlistView}`;
        }
        return content;
    }
    else if (action === "rewriteComponentProperties") {
        let content = "";
        appliedActionData.componentPropertiesUpdated?.map((prop) => {
            // eslint-disable-next-line @buildwithflux/no-array-foreach
            prop.propertiesUpdated?.forEach((property) => {
                const unit = property.unit ?? propertyDefinitionsByLabel[property.name]?.unit;
                const displayValue = formatValueWithUnit(property.value, unit ?? undefined);
                content += `- Set ${prop.componentDesignator}'s ${property.name} to "${truncateValueString(displayValue)}"\n`;
            });
            // eslint-disable-next-line @buildwithflux/no-array-foreach
            prop.propertiesRemoved?.forEach((propertyName) => {
                content += `- Removed ${prop.componentDesignator}'s ${propertyName} property\n`;
            });
        });
        return content;
    }
    else if (action === "rewriteDesignators") {
        let content = "";
        for (const [oldDesignator, newDesignator] of Object.entries(appliedActionData.componentDesignatorsUpdated ?? {})) {
            content += `- Renamed ${oldDesignator} to ${newDesignator}\n`;
        }
        for (const [oldDesignator, newDesignator] of Object.entries(appliedActionData.netDesignatorsUpdated ?? {})) {
            content += `- Renamed ${oldDesignator} to ${newDesignator}\n`;
        }
        return content;
    }
    else if (action === "rewriteLayoutRules") {
        let content = "";
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        (appliedActionData.pcbLayoutRulesUpdated ?? []).forEach((ruleUpdate) => {
            const node = document?.pcbLayoutNodes[ruleUpdate.nodeUid];
            if (node) {
                // eslint-disable-next-line @buildwithflux/no-array-foreach
                ruleUpdate.rulesUpdated?.forEach((rule) => {
                    content += `- Set ${node.name}'s ${rule.key} to "${truncateValueString(rule.value)}"\n`;
                });
                // eslint-disable-next-line @buildwithflux/no-array-foreach
                ruleUpdate.rulesRemoved?.forEach((ruleKey) => {
                    content += `- Removed ${node.name}'s ${ruleKey} layout rule\n`;
                });
            }
        });
        return content;
    }
    else if (action === "rewriteProjectDescription") {
        return `Updated to: "${appliedActionData.projectDescription}"`;
    }
    else if (action === "rewriteProjectProperties") {
        let content = "";
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        appliedActionData.projectPropertiesAdded?.forEach((property) => {
            content += `- Set ${property.name} to "${truncateValueString(property.value)}"\n`;
        });
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        appliedActionData.projectPropertiesRemoved?.forEach((propertyName) => {
            content += `- Removed ${propertyName}\n`;
        });
        return content;
    }
}
//# sourceMappingURL=determineActionGenerationMessageContent.js.map